import React, { useState, useEffect, useRef } from 'react';
import ReusableTable from '../../dashboard/ReusableTable';
import ReusableForm from '../../common/ReusableForm';
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { getRequest, postRequest, putRequest, deleteRequest } from '../../services/apiService';
import { Dropdown } from 'primereact/dropdown';


const BlockTransaction = () => {
  const [data, setData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [selectedProject, setSelectedProject] = useState(1);
  const [addDialogVisible, setAddDialogVisible] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    project: 1,
    blocks: [],
    blockNames: {} // Changed from single name to a map of block ID -> name
  });
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: '' });
  
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  // Fetch projects for dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getRequest('master/projects/');
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    fetchProjects();
  }, []);

  // Fetch all blocks from paginated API
  useEffect(() => {
    const fetchAllBlocks = async () => {
      try {
        let allBlocks = [];
        let nextUrl = 'master/mpr-block-master/';
        
        while (nextUrl) {
          const response = await getRequest(nextUrl);
          allBlocks = [...allBlocks, ...response.results.data];
          nextUrl = response.next;
        }
        
        setBlocks(allBlocks);
      } catch (error) {
        console.error('Error fetching blocks:', error);
      }
    };
    fetchAllBlocks();
  }, []);

  // Function to open the modal for adding a new block transaction
  const openAddDialog = () => {
    setCurrentItem(null);
    setFormData({
      project: selectedProject, // Default to current selected project
      blocks: [],
      blockNames: {}
    });
    setAddDialogVisible(true);
  };

  // Debounced search effect for block transactions
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      getRequest(`transaction/block-transaction/?project_id=${selectedProject}&search=${encodeURIComponent(search)}`)
        .then(response => {
          setData(response);
        })
        .catch(error => {
          console.error('Error fetching block transactions:', error);
        });
    }, 700);
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [selectedProject, search]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  // Generate dynamic fields for block names
  const dynamicBlockNameFields = formData.blocks.map(blockId => {
    const block = blocks.find(b => b.id === blockId);
    return {
      name: `blockName_${blockId}`,
      label: block ? `${block.name} (${block.code})` : `Block ID: ${blockId}`,
      type: 'text',
      required: true,
      placeholder: 'Enter block name',
      value: formData.blockNames[blockId] || ''
    };
  });

  // Main form fields
  const formFields = [
    {
      name: 'project',
      label: 'Project',
      type: 'select',
      options: projects.map(project => ({
        label: project.name,
        value: project.id
      })),
      required: true
    },
    {
      name: 'blocks',
      label: 'Blocks',
      type: 'multiselect',
      options: blocks.map(block => ({
        label: `${block.name} (${block.code})`,
        value: block.id
      })),
      required: true
    },
    ...dynamicBlockNameFields
  ];

  // Handle form changes for block names
  const handleReusableFormChange = (name, value) => {
    if (typeof name === 'string' && name.startsWith('blockName_')) {
      const blockId = parseInt(name.replace('blockName_', ''));
      setFormData(prev => ({
        ...prev,
        blockNames: {
          ...prev.blockNames,
          [blockId]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle submit for add/edit
  const handleReusableFormSubmit = async () => {
    try {
      if (currentItem) {
        // Edit existing entry
        await putRequest(`transaction/block-transaction/${currentItem.id}/`, {
          project: formData.project,
          name: formData.blockNames[formData.blocks[0]] || '',
          block: formData.blocks[0],
          status_db: 1
        });
      } else {
        // Create new entries for each selected block
        const payload = formData.blocks.map(blockId => ({
          project: formData.project,
          name: formData.blockNames[blockId] || '',
          block: blockId,
          status_db: 1
        }));
        await postRequest('transaction/block-transaction/', payload);
      }
      // Refresh data
      const response = await getRequest(
        `transaction/block-transaction/?project_id=${selectedProject}`
      );
      setData(response);
      setAddDialogVisible(false);
      setEditDialogVisible(false);
      setFormData({
        project: selectedProject,
        blocks: [],
        blockNames: {}
      });
      setCurrentItem(null);
    } catch (error) {
      console.error('Error saving block transaction:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRequest(`transaction/block-transaction/${currentItem.id}/`);
      setData(data.filter(item => item.id !== currentItem.id));
      setDeleteDialogVisible(false);
    } catch (error) {
      console.error('Error deleting block transaction:', error);
    }
  };

  const actionItems = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (rowData) => {
        setCurrentItem(rowData);
        setFormData({
          project: rowData.project.id,
          blocks: [rowData.block.id],
          blockNames: { [rowData.block.id]: rowData.name }
        });
        setEditDialogVisible(true);
      }
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: (rowData) => {
        setCurrentItem(rowData);
        setDeleteDialogVisible(true);
      }
    }
  ];

  const columnFields = [
    { 
      field: 'name', 
      header: 'Block Name',
      body: (rowData) => rowData.name
    },
    // { 
    //   field: 'block.code', 
    //   header: 'Block Code',
    //   body: (rowData) => rowData.block?.code || 'N/A'
    // },
    { 
      field: 'project.name', 
      header: 'Project Name',
      body: (rowData) => rowData.project?.name || 'N/A'
    }
  ];

  return (
    <div className="p-4">
      <div className="mb-4 flex align-items-center">
        <label htmlFor="projectFilter" className="mr-2 font-bold">Project:</label>
        <Dropdown
          id="projectFilter"
          value={selectedProject}
          options={projects.map(project => ({
            label: project.name,
            value: project.id
          }))}
          onChange={(e) => {
            setSelectedProject(e.value);
            setFormData(prev => ({ ...prev, project: e.value }));
          }}
          placeholder="Select Project"
          style={{ width: '200px' }}
        />
      </div>

      <ReusableTable
        title="Block Transactions"
        data={data}
        setData={setData}
        columnFields={columnFields}
        setAddNewDialogVisible={openAddDialog}
        actionMenu={true}
        actionItems={actionItems}
        rowdelete={false}
        searchValue={search}
        onSearchChange={handleTableSearch}
      />

      {/* Add/Edit Modal with explicit wrapper */}
      {(addDialogVisible || editDialogVisible) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-auto animate-fade-in">
            <ReusableForm
              headerText={currentItem ? 'Edit Block Transaction' : 'Add New Block Transaction'}
              formData={formData}
              setFormData={setFormData}
              fields={formFields}
              onChange={handleReusableFormChange}
              onSubmit={handleReusableFormSubmit}
              onHide={() => {
                setAddDialogVisible(false);
                setEditDialogVisible(false);
                setFormData({
                  project: selectedProject,
                  blocks: [],
                  blockNames: {}
                });
                setCurrentItem(null);
              }}
              loading={false}
            />
          </div>
        </div>
      )}

      <ReusableDeleteDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onDelete={handleDelete}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={false}
        title="Delete Block Transaction"
        description={`Are you sure you want to delete this block transaction? This action cannot be undone. Type 'DELETE' to confirm.`}
        deleteLabel="Delete"
      />
    </div>
  );
};

export default BlockTransaction;